import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Button,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LinkIcon from "@mui/icons-material/Link";
import styles from "./AddCarouselSlider.module.scss";
import { api } from "../../../../services";
import { CONSTANTS } from "../../../../library/common/constant";
import { throttle } from "lodash";

const {
  IMAGE_PREVIEW_TITLE,
  ALT_TEXT_LABEL,
  LINK_URL_LABEL,
  BANNER_TITLE,
  BANNER_TITLE_HINDI,
  DESCRIPTION_LABEL,
  DESCRIPTION_LABEL_HINDI,
  ADD_SLIDER_BUTTON,
  UPDATE_SLIDER_BUTTON,
  THROTTLE_DURATION,
} = CONSTANTS;

const AddCarouselSlider = () => {
  const navigate = useNavigate();
  const { id: bannerId } = useParams();

  const [formData, setFormData] = useState({
    altText: "",
    redirectUrl: "",
    imageUrl: "",
    title: { en: "", hi: "" },
    description: { en: "", hi: "" },
    path: "home",
    isActive: false,
    imageFile: null,
  });

  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.imageFile && !formData?.imageUrl) {
      newErrors.imageUrl = "Image file or URL is required";
    } else if (
      formData?.imageUrl &&
      !/^https?:\/\/.+/.test(formData?.imageUrl)
    ) {
      newErrors.imageUrl = "Enter a valid image URL";
    }

    if (!formData?.altText) {
      newErrors.altText = "Alt text is required";
    }

    if (!formData?.redirectUrl) {
      newErrors.redirectUrl = "Link URL is required";
    } else if (!/^https?:\/\/.+/.test(formData?.redirectUrl)) {
      newErrors.redirectUrl = "Enter a valid URL starting with http(s)://";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    let updatedFormData = { ...formData };
    let updatedErrors = { ...errors };

    if (name === "imageFile" && files?.length) {
      const file = files[0];
      updatedFormData.imageFile = file;
      setPreviewUrl(URL.createObjectURL(file));
      if (updatedErrors?.imageFile) updatedErrors.imageFile = "";
    } else if (name.includes(".")) {
      const [key, lang] = name.split(".");
      updatedFormData[key] = {
        ...updatedFormData[key],
        [lang]: value,
      };
      if (updatedErrors[key]) updatedErrors[key] = "";
    } else {
      updatedFormData[name] = type === "checkbox" ? checked : value;
      if (updatedErrors[name]) updatedErrors[name] = "";
    }

    setFormData(updatedFormData);
    setErrors(updatedErrors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const form = new FormData();
    form.append("altText", formData?.altText);
    form.append("redirectUrl", formData?.redirectUrl);
    form.append("isActive", formData?.isActive);
    form.append("path", formData?.path);
    form.append("title", JSON.stringify(formData?.title));
    form.append("description", JSON.stringify(formData?.description));

    if (formData?.imageFile) {
      form.append("image", formData?.imageFile);
    } else {
      form.append("imageUrl", formData?.imageUrl);
    }

    formRef.current = form;

    try {
      if (bannerId) {
        handleEdit();
      } else {
        handleAdd();
      }
    } catch (err) {
      console.error("Banner submit error:", err);
    }
  };

  const returnBack = () => {
    navigate("/banner/list", { replace: true });
  };

  const handleAdd = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.post("/banners", form);
      returnBack();
    } catch (error) {
      console.error("Failed to post: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEdit = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.patch(`/banners/${bannerId}`, form);
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  useEffect(() => {
    if (!bannerId) return;

    const fetchBannerDetails = async () => {
      const { data } = await api.get(`/banners/${bannerId}`);
      setFormData({
        ...data,
        imageFile: null,
      });
      setPreviewUrl(data?.imageUrl || "");
    };

    fetchBannerDetails();
  }, [bannerId]);

  return (
    <Paper className={styles.carouselAdd}>
      <Typography variant="h5" className={styles.title}>
        {bannerId ? "Update Carousel Slider" : "Add Carousel Slider"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box className={styles.formContainer}>
          <Box className={styles.imagePreview}>
            <label>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className={styles.previewImage}
                />
              ) : (
                <Box className={styles.previewIcon}>
                  <AddPhotoAlternateIcon className={styles.icon} />
                  <Typography variant="body2" color="textSecondary">
                    {IMAGE_PREVIEW_TITLE}
                  </Typography>
                </Box>
              )}
              <input
                type="file"
                name="imageFile"
                accept="image/*"
                onChange={handleChange}
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </label>
          </Box>

          <TextField
            fullWidth
            label={ALT_TEXT_LABEL}
            name="altText"
            value={formData?.altText || ""}
            onChange={handleChange}
            error={!!errors?.altText}
            helperText={errors?.altText}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AddPhotoAlternateIcon color="action" />
                </InputAdornment>
              ),
            }}
            className={styles.textField}
          />

          <TextField
            fullWidth
            label={LINK_URL_LABEL}
            name="redirectUrl"
            value={formData?.redirectUrl || ""}
            onChange={handleChange}
            error={!!errors?.redirectUrl}
            helperText={errors?.redirectUrl}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="action" />
                </InputAdornment>
              ),
            }}
            className={styles.textField}
          />

          <TextField
            fullWidth
            label={BANNER_TITLE}
            name="title.en"
            value={formData?.title?.en || ""}
            onChange={handleChange}
            className={styles.textField}
          />
          <TextField
            fullWidth
            label={BANNER_TITLE_HINDI}
            name="title.hi"
            value={formData?.title?.hi || ""}
            onChange={handleChange}
            className={styles.textField}
          />
          <TextField
            fullWidth
            label={DESCRIPTION_LABEL}
            name="description.en"
            value={formData?.description?.en || ""}
            onChange={handleChange}
            multiline
            minRows={3}
            className={styles.textField}
          />
          <TextField
            fullWidth
            label={DESCRIPTION_LABEL_HINDI}
            name="description.hi"
            value={formData?.description?.hi || ""}
            onChange={handleChange}
            multiline
            minRows={3}
            className={styles.textField}
          />

          <TextField
            fullWidth
            select
            label="Path"
            name="path"
            value={formData?.path || ""}
            onChange={handleChange}
            SelectProps={{ native: true }}
            className={styles.textField}
          >
            <option value="home">Home</option>
            <option value="book">Book</option>
            <option value="article">Article</option>
            <option value="blog">Blog</option>
            <option value="tirth">Tirth</option>
          </TextField>

          <Box display="flex" alignItems="center" gap={1}>
            <input
              type="checkbox"
              name="isActive"
              checked={formData?.isActive || false}
              onChange={handleChange}
            />
            <Typography variant="body1">Is Active</Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {bannerId ? UPDATE_SLIDER_BUTTON : ADD_SLIDER_BUTTON}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddCarouselSlider;
